import React, { KeyboardEvent, MouseEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons/faLocationDot';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons/faEllipsisVertical';

import { Sitter } from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { StarRating } from '../Rating/StarRating';
import { ProfileVideoModal } from '../ProfileVideoModal/ProfileVideoModal';
import { isEnterKey } from '../utils/a11y';
import { BadgesRow } from './BadgesRow';
import { SitterSearchPhoto } from './SitterSearchPhoto';
import {
  getBadgeDisplayCount,
  getDistanceText,
  getGroupConnections,
  getHighlightText,
  getIndirectConnections,
  getSelfBadge,
  getSitterBadges,
  truncate,
} from './utils';

const ABOUT_EXCERPT_LENGTH = 90;

export interface SitterSearchCardProps {
  sitter: Sitter;
  bookCta?: string;
  highlightText?: string;
  isPassList?: boolean;
  showSavedSearchActions?: boolean;
  userPictureUrl?: string;
  onCardClick(sitter: Sitter): void;
  onBook?: (sitter: Sitter) => void;
  onMessage?: (sitter: Sitter) => void;
  onInterview?: (sitter: Sitter) => void;
  onPass?: (sitter: Sitter) => void;
  onUnpass?: (sitter: Sitter) => void;
  onToggleFavorite?: (sitter: Sitter) => void | Promise<void>;
}

const useDropdown = (): {
  open: boolean;
  toggle: (event: MouseEvent | KeyboardEvent) => void;
  close: () => void;
  ref: React.RefObject<HTMLDivElement>;
} => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  React.useEffect((): (() => void) | undefined => {
    if (!open) return undefined;
    const handler = (event: Event): void => {
      const target = event.target as Node | null;
      if (target && ref.current && !ref.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return (): void => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (event: MouseEvent | KeyboardEvent): void => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };
  const close = (): void => setOpen(false);

  return { open, toggle, close, ref };
};

export const SitterSearchCard = ({
  sitter,
  bookCta,
  highlightText,
  isPassList,
  showSavedSearchActions = false,
  userPictureUrl,
  onCardClick,
  onBook,
  onMessage,
  onInterview,
  onPass,
  onUnpass,
  onToggleFavorite,
}: SitterSearchCardProps): JSX.Element => {
  const { t } = useTranslation();
  const dropdown = useDropdown();
  const [videoOpen, setVideoOpen] = useState(false);

  const {
    firstName,
    relationship,
    rating,
    reviewCount,
    address,
    userStats,
    about,
  } = sitter;

  const distance = relationship?.distance;
  const distanceText = getDistanceText(t, distance);
  const distanceValue = typeof distance === 'string' ? parseFloat(distance) : (distance ?? 0);
  const distanceWarn = !Number.isNaN(distanceValue) && distanceValue > 50;

  const minimumRate = sitter.displayRate?.minimumRate ?? 0;

  const sitterBadges = getSitterBadges(t, sitter);
  const selfBadge = getSelfBadge(t, sitter);
  const hasSelfBadge = Boolean(selfBadge && userPictureUrl);
  const indirectConnections = getIndirectConnections(t, sitter);
  const groupConnections = getGroupConnections(t, sitter, indirectConnections.length, hasSelfBadge);
  const { displayCount, overflowCount } = getBadgeDisplayCount(
    sitter,
    indirectConnections.length,
    hasSelfBadge,
    sitterBadges.length,
  );

  const resolvedHighlight = highlightText ?? getHighlightText(t, sitter);
  const aboutExcerpt = about ? truncate(about, ABOUT_EXCERPT_LENGTH) : '';
  const lastAccess = userStats?.lastAccessLabel;

  const handleCardClick = (event: MouseEvent | KeyboardEvent): void => {
    if ((event.target as HTMLElement | null)?.closest('[data-stop-card-click]')) return;
    onCardClick(sitter);
  };

  const handleNameClick = (event: MouseEvent | KeyboardEvent): void => {
    event.stopPropagation();
    onCardClick(sitter);
  };

  const handleBook = (event: MouseEvent): void => {
    event.stopPropagation();
    onBook?.(sitter);
  };

  const runMenuAction = (action?: (s: Sitter) => void) => (event: MouseEvent): void => {
    event.stopPropagation();
    dropdown.close();
    action?.(sitter);
  };

  const showMenu = Boolean(onMessage || onInterview || (showSavedSearchActions && (onPass || onUnpass)));
  const passLabel = isPassList
    ? t('components.sitterSearchCard.menu.show')
    : t('components.sitterSearchCard.menu.pass');
  const passHandler = isPassList ? onUnpass : onPass;
  const resolvedBookCta = bookCta ?? t('components.sitterSearchCard.book');

  return (
    <>
      {/* Mouse-only click target for the whole card; keyboard users navigate via the
          name button below, which owns the accessible profile entry point. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        id={`sitter-card-${sitter.id ?? ''}`}
        className='relative w-full bg-white border border-gray-300 rounded-lg p-4 shadow-sm hover:shadow-md cursor-pointer transition-shadow flex flex-col'
        onClick={handleCardClick}
      >
        <div className='flex flex-row gap-4 items-start'>
          <SitterSearchPhoto
            sitter={sitter}
            onPlayVideo={sitter.vimeoId ? (): void => setVideoOpen(true) : undefined}
            onToggleFavorite={onToggleFavorite}
          />
          <div className='flex-1 min-w-0'>
            <div className='flex justify-between items-start gap-2'>
              <button
                type='button'
                onClick={handleNameClick}
                onKeyDown={(event): void => { if (isEnterKey(event)) handleNameClick(event); }}
                className='text-xl font-semibold truncate text-left bg-transparent border-0 p-0 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sapphire-500 rounded'
                aria-label={t('components.sitterSearchCard.ariaLabel', { sitterName: firstName ?? '' })}
                data-stop-card-click
              >
                {firstName}
              </button>
              {showMenu && (
                <div ref={dropdown.ref} className='relative' data-stop-card-click>
                  <button
                    type='button'
                    aria-haspopup='menu'
                    aria-expanded={dropdown.open}
                    aria-label={t('components.sitterSearchCard.menu.ariaLabel', { sitterName: firstName ?? '' })}
                    onClick={dropdown.toggle}
                    onKeyDown={(event): void => { if (isEnterKey(event)) dropdown.toggle(event); }}
                    className='w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100'
                  >
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </button>
                  {dropdown.open && (
                    <div
                      role='menu'
                      className='absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1'
                    >
                      {onMessage && (
                        <button
                          type='button'
                          role='menuitem'
                          id={`message-link-${sitter.id ?? ''}`}
                          onClick={runMenuAction(onMessage)}
                          className='w-full text-left px-4 py-2 text-sm hover:bg-gray-100'
                        >
                          {t('components.sitterSearchCard.menu.message')}
                        </button>
                      )}
                      {onInterview && (
                        <button
                          type='button'
                          role='menuitem'
                          id={`interview-link-${sitter.id ?? ''}`}
                          onClick={runMenuAction(onInterview)}
                          className='w-full text-left px-4 py-2 text-sm hover:bg-gray-100'
                        >
                          {t('components.sitterSearchCard.menu.interview')}
                        </button>
                      )}
                      {showSavedSearchActions && passHandler && (
                        <button
                          type='button'
                          role='menuitem'
                          id={`pass-link-${sitter.id ?? ''}`}
                          onClick={runMenuAction(passHandler)}
                          className='w-full text-left px-4 py-2 text-sm hover:bg-gray-100'
                        >
                          {passLabel}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {(rating ?? 0) > 0 && (
              <div className='mt-1'>
                <StarRating
                  rating={rating ?? 0}
                  reviewCount={reviewCount ?? 0}
                  size='sm'
                />
              </div>
            )}
            {minimumRate > 0 && (
              <div className='mt-1 text-lg font-semibold'>
                {t('components.sitterSearchCard.rate', { rate: minimumRate })}
              </div>
            )}
          </div>
        </div>

        <div className='mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm'>
          {distanceText && (
            <span className={`flex items-center gap-1 ${distanceWarn ? 'text-red-600' : ''}`}>
              <FontAwesomeIcon icon={faLocationDot} className='text-xs' />
              <span>{distanceText}</span>
              {address?.city && <span>- {address.city}</span>}
            </span>
          )}
          {lastAccess && (
            <span className='flex items-center gap-1 text-gray-700'>
              <FontAwesomeIcon icon={faBolt} className='text-xs' />
              <span className='lowercase'>{t('components.sitterSearchCard.activeAgo', { activeAgo: lastAccess })}</span>
            </span>
          )}
        </div>

        <BadgesRow
          selfBadge={selfBadge}
          connections={indirectConnections}
          groupConnections={groupConnections}
          badges={sitterBadges}
          badgeDisplayCount={displayCount}
          overflowCount={overflowCount}
          userPictureUrl={userPictureUrl}
        />

        {(resolvedHighlight || aboutExcerpt) && (
          <div className='mt-3 hidden md:block'>
            {resolvedHighlight && (
              <h4 className='font-semibold text-base mb-1'>{resolvedHighlight}</h4>
            )}
            {aboutExcerpt && (
              <p className='text-sm text-gray-700'>{aboutExcerpt}</p>
            )}
          </div>
        )}

        {onBook && (
          <div className='mt-auto pt-4'>
            <Button
              id={`book-link-${sitter.id ?? ''}`}
              buttonType={ButtonType.primary}
              onClick={handleBook}
              className='w-full !px-0'
              data-stop-card-click
            >
              {resolvedBookCta}
            </Button>
          </div>
        )}
      </div>
      {sitter.vimeoId && videoOpen && (
        <ProfileVideoModal
          isOpen
          onClose={(): void => setVideoOpen(false)}
          vimeoId={sitter.vimeoId}
          title={firstName ? `${firstName}'s video` : undefined}
        />
      )}
    </>
  );
};
